"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var buildDefaultFilterInfo_1 = __importDefault(require("./lib/buildDefaultFilterInfo"));
var getOffsetFromPage_1 = __importDefault(require("./lib/getOffsetFromPage"));
var getPageFromOffset_1 = __importDefault(require("./lib/getPageFromOffset"));
var shallowEqual_1 = __importDefault(require("./lib/shallowEqual"));
var store_1 = require("./store");
function useFilter(namespace, options) {
    // Having these types of refs helps get around rules of hooks, and they need
    // to be updated each render.
    var ctxRefValue = {
        namespace: namespace,
        onChange: options.onChange,
        debounceDuration: options.debounceDuration,
        defaultFilterInfo: options.defaultFilterInfo,
        onBeforeSaveFilter: options.onBeforeSaveFilter,
        store: options.store,
    };
    var ctxRef = react_1.useRef(ctxRefValue);
    react_1.useEffect(function () {
        ctxRef.current = ctxRefValue;
    });
    var _a = react_1.useState(function () {
        var _a;
        var fromStore = store_1.getFilterStore(ctxRef.current.store).getFilter(namespace);
        // If the filter is already in the store, we have to make sure it ...
        // - has both the filter and filterInfo
        // - has all the fields the user passed to the hook
        if (fromStore) {
            var filterInfo_1 = buildDefaultFilterInfo_1.default(__assign(__assign({}, fromStore), { shouldRunImmediately: true, filter: __assign(__assign({}, (_a = options.defaultFilterInfo) === null || _a === void 0 ? void 0 : _a.filter), fromStore.filter) }));
            return filterInfo_1;
        }
    }), filterInfo = _a[0], setFilterInfoState = _a[1];
    var _b = react_1.useState(function () {
        return store_1.getFilterStore(ctxRef.current.store).getData(namespace);
    }), data = _b[0], setData = _b[1];
    var _c = react_1.useState(!filterInfo), isLoading = _c[0], setIsLoading = _c[1];
    var doesFilterExist = react_1.useRef(!!filterInfo);
    var lastRefreshAtRef = react_1.useRef(
    // if shouldForceRunOnMount is set, always use -1
    // otherwise, try grabbing lastRefreshAt
    // then, just use -1 cause there's no other option
    options.shouldForceRunOnMount
        ? -1
        : filterInfo
            ? filterInfo.lastRefreshAt
            : -1);
    var updateReasonRef = react_1.useRef('initial');
    // Call onChange when data changes.
    react_1.useEffect(function () {
        setIsLoading(true);
        // If there is no existing filter info, set it to the defaults and return.
        // This same effect will trigger next go.
        if (!filterInfo) {
            setFilterInfoState(__assign(__assign({}, buildDefaultFilterInfo_1.default(ctxRef.current.defaultFilterInfo)), { shouldRunImmediately: true }));
            return;
        }
        // If the lastRefreshAt hasn't changed, don't make a request.
        if (lastRefreshAtRef.current === filterInfo.lastRefreshAt) {
            setIsLoading(false);
            return;
        }
        var makeRequest = function () {
            lastRefreshAtRef.current = filterInfo.lastRefreshAt;
            var response = ctxRef.current.onChange(filterInfo, updateReasonRef.current);
            store_1.getFilterStore(ctxRef.current.store).saveFilter(namespace, ctxRef.current.onBeforeSaveFilter
                ? ctxRef.current.onBeforeSaveFilter(filterInfo)
                : filterInfo);
            if (!response) {
                setIsLoading(false);
                return;
            }
            function handleResponse(response) {
                if (!response) {
                    return;
                }
                if (response.filterInfo) {
                    setFilterInfoState(function (previous) {
                        // Even though we've already checked for response.filterInfo, since
                        // we're in a callback TypeScript still considers accessing it
                        // unsafe, so do another check.
                        invariant(!!response.filterInfo, '');
                        invariant(previous != null, 'handleResponse called without filterInfo');
                        var extra = {};
                        var _a = response.filterInfo, page = _a.page, offset = _a.offset, totalResults = _a.totalResults, pageSize = _a.pageSize, nextCursor = _a.nextCursor, filterInfoFromResponse = __rest(_a, ["page", "offset", "totalResults", "pageSize", "nextCursor"]);
                        var pageSizeNumber = Number(pageSize || previous.pageSize);
                        if (page != null && offset == null) {
                            extra.offset = getOffsetFromPage_1.default(page, pageSizeNumber);
                            extra.page = page;
                            // If there's an offset but no page, set the page.
                        }
                        else if (page == null && offset != null) {
                            var offsetNumber = Number(offset);
                            extra.offset = offsetNumber;
                            extra.page = getPageFromOffset_1.default(offsetNumber, pageSizeNumber);
                        }
                        // `null` / `undefined` are valid here, it means there is zero
                        // results, so we can't just check if it's truthy, we need to check
                        // for presence.
                        if ('totalResults' in response.filterInfo) {
                            extra.totalResults = Number(totalResults || 0);
                            extra.totalPages = Math.ceil(extra.totalResults / pageSizeNumber);
                        }
                        // `null` / `undefined` are valid here, it means there is no next
                        // cursor, so we can't just check if it's truthy, we need to check
                        // for presence.
                        if ('nextCursor' in response.filterInfo) {
                            extra.nextCursor = nextCursor;
                        }
                        return __assign(__assign(__assign({}, previous), filterInfoFromResponse), extra);
                    });
                }
                if (response.data) {
                    setData(response.data);
                    store_1.getFilterStore(ctxRef.current.store).saveData(namespace, response.data);
                }
            }
            if (isPromise(response)) {
                response.then(handleResponse).finally(function () { return setIsLoading(false); });
            }
            else {
                handleResponse(response);
                setIsLoading(false);
            }
        };
        var timeout = undefined;
        if (filterInfo.shouldRunImmediately) {
            makeRequest();
        }
        else {
            timeout = setTimeout(makeRequest, ctxRef.current.debounceDuration != null
                ? ctxRef.current.debounceDuration
                : 500);
        }
        return function () {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [filterInfo, namespace]);
    var api = {
        isLoading: isLoading,
        data: data,
        doesFilterExist: doesFilterExist.current,
        filterInfo: filterInfo || buildDefaultFilterInfo_1.default(options.defaultFilterInfo),
        updateReason: updateReasonRef.current,
        updateFilter: react_1.useCallback(function (filter, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'filter';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'updateFilter called without filterInfo');
                var nextFilter = __assign(__assign({}, previous.filter), filter);
                if (shallowEqual_1.default(previous.filter, nextFilter)) {
                    return previous;
                }
                return __assign(__assign({}, previous), { offset: 0, page: 1, totalResults: 1, totalPages: 1, filter: nextFilter, lastRefreshAt: new Date().getTime(), cursor: undefined, nextCursor: undefined, shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        resetFilter: react_1.useCallback(function (shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'filter';
            setFilterInfoState(__assign(__assign({}, buildDefaultFilterInfo_1.default(ctxRef.current.defaultFilterInfo)), { lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately }));
        }, []),
        setOffset: react_1.useCallback(function (offset, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'pagination';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'setOffset called without filterInfo');
                var offsetNumber = Number(offset);
                return __assign(__assign({}, previous), { offset: offsetNumber, page: getPageFromOffset_1.default(offsetNumber, previous.pageSize), lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        setPage: react_1.useCallback(function (page, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'pagination';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'setPage called without filterInfo');
                var pageNumber = Number(page);
                return __assign(__assign({}, previous), { offset: getOffsetFromPage_1.default(pageNumber, previous.pageSize), page: pageNumber, lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        setPageSize: react_1.useCallback(function (pageSize, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'pagination';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'setPageSize called without filterInfo');
                var pageSizeNumber = Number(pageSize);
                var page = getPageFromOffset_1.default(previous.offset, pageSizeNumber);
                var offset = getOffsetFromPage_1.default(page, pageSizeNumber);
                return __assign(__assign({}, previous), { pageSize: pageSizeNumber, offset: offset, page: page, lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        setSort: react_1.useCallback(function (sort, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'filter';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'setSort called without filterInfo');
                return __assign(__assign({}, previous), { sort: sort, lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        setCursor: react_1.useCallback(function (cursor, shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            setFilterInfoState(function (previous) {
                updateReasonRef.current = 'pagination';
                invariant(previous != null, 'setCursor called without filterInfo');
                return __assign(__assign({}, previous), { cursor: cursor, lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
        forceRefresh: react_1.useCallback(function (shouldRunImmediately) {
            if (shouldRunImmediately === void 0) { shouldRunImmediately = false; }
            updateReasonRef.current = 'filter';
            setFilterInfoState(function (previous) {
                invariant(previous != null, 'forceRefresh called without filterInfo');
                return __assign(__assign({}, previous), { lastRefreshAt: new Date().getTime(), shouldRunImmediately: shouldRunImmediately });
            });
        }, []),
    };
    return api;
}
exports.default = useFilter;
function invariant(input, message) {
    if (!input) {
        throw new Error("Invariant error: " + message);
    }
}
function isPromise(t) {
    return !!(t === null || t === void 0 ? void 0 : t.then);
}
